/**
 * Fila offline e drenagem (spec 5.1, 10.6 e 14).
 *
 * A regra que organiza este arquivo: **falha de REDE enfileira; resposta do
 * SERVIDOR nao.** Um 400 significa que a operacao esta errada e vai continuar
 * errada — reenfileira-la criaria uma fila que nunca escoa e esconderia o erro
 * do usuario. So o caso em que a requisicao nao chegou a lugar nenhum vira
 * pendencia.
 */

import { API_PREFIX, uuidv7, type SyncResponse } from '@cleartask/shared';
import { ApiClientError } from './api.js';
import { anunciarSessaoExpirada, anunciarSessaoValida, ehDesvioDeLogin } from './sessao.js';
import {
  bumpAttempts,
  countPendingOps,
  enqueueOp,
  pendingOps,
  readSyncSince,
  removeOps,
  restampOp,
  writeSyncSince,
  type QueuedOp,
} from './db.js';

const DEV_EMAIL = import.meta.env['VITE_DEV_EMAIL'] as string | undefined;

/** Descricao de uma escrita, sem os campos que esta camada preenche. */
export interface OpDraft {
  entity: QueuedOp['entity'];
  action: QueuedOp['action'];
  entityId: string;
  payload: unknown;
}

type Listener = () => void;

const listeners = new Set<Listener>();

/** Avisa a UI que a fila mudou de tamanho (spec 10.6). */
function notificar(): void {
  for (const listener of listeners) listener();
}

export function subscribeToQueue(listener: Listener): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

export function queueSize(): Promise<number> {
  return countPendingOps();
}

/** Ultimo carimbo emitido, para nao repetir dentro do mesmo milissegundo. */
let ultimoQueuedAt = 0;

/**
 * Carimbo estritamente crescente.
 *
 * `Date.now()` puro nao serve como ordem de intencao: duas escritas no mesmo
 * milissegundo empatam, e a fila passa a sair em ordem arbitraria. Nao e
 * hipotetico — o desfazer de uma conclusao dispara um `update` e um `delete` em
 * sequencia, e os dois cabem folgadamente no mesmo milissegundo.
 *
 * Empurrar 1 ms para a frente mantem o valor colado ao relogio de parede (que e
 * o que o servidor compara com `updated_at` para detectar edicao concorrente) e
 * torna a ordem determinista. O desvio maximo e o tamanho da rajada.
 */
function proximoQueuedAt(): number {
  ultimoQueuedAt = Math.max(Date.now(), ultimoQueuedAt + 1);
  return ultimoQueuedAt;
}

/** Etiqueta do Background Sync. Precisa ser a mesma lida em `sw.ts`. */
export const SYNC_TAG = 'cleartask-outbox';

/**
 * Pede ao navegador que drene a fila quando houver rede (fase 7b).
 *
 * E o que faz a escrita offline sobreviver ao FECHAMENTO da aba: o evento
 * `sync` dispara no service worker mesmo com o app fechado. Ate a fase 7 a
 * drenagem dependia de `online`/`visibilitychange`, que exigem a aba viva —
 * quem escrevia no metro e fechava o app ficava com a escrita presa.
 *
 * Falha em silencio de proposito. O Background Sync nao existe no Safari nem no
 * Firefox, e a ausencia dele nao e um erro a reportar: os disparos por evento
 * continuam valendo em todo navegador. Isto e um reforco, nao o mecanismo
 * principal.
 */
async function pedirSyncEmSegundoPlano(): Promise<void> {
  if (typeof navigator === 'undefined' || !('serviceWorker' in navigator)) return;

  try {
    const registro = await navigator.serviceWorker.ready;
    await (registro as ServiceWorkerRegistration & {
      sync?: { register: (tag: string) => Promise<void> };
    }).sync?.register(SYNC_TAG);
  } catch {
    // Sem suporte, ou permissao de background negada. Segue o baile.
  }
}

export async function enqueue(draft: OpDraft): Promise<void> {
  await enqueueOp({ ...draft, opId: uuidv7(), queuedAt: proximoQueuedAt(), attempts: 0 });
  notificar();
  void pedirSyncEmSegundoPlano();
}

/**
 * A requisicao falhou por falta de rede, e nao por recusa do servidor?
 *
 * `fetch` rejeita com `TypeError` quando nao consegue completar a requisicao.
 * Um `ApiClientError` significa o oposto: houve resposta, com status e corpo —
 * o servidor esta vivo e disse nao.
 */
function ehFalhaDeRede(err: unknown): boolean {
  return !(err instanceof ApiClientError);
}

/**
 * Marcador de "foi para a fila", devolvido no lugar da resposta do servidor.
 *
 * E um tipo proprio, e nao um `null` disfarcado de resposta: quem consome o
 * resultado da mutacao precisa PODER distinguir "o servidor confirmou" de "esta
 * pendente", e o compilador obriga a tratar o caso. Um `null` com cast passaria
 * batido ate estourar em producao, offline, que e onde ninguem esta olhando.
 */
export const QUEUED_OFFLINE = { queuedOffline: true } as const;
export type QueuedOffline = typeof QUEUED_OFFLINE;

export function isQueuedOffline(value: unknown): value is QueuedOffline {
  return typeof value === 'object' && value !== null && 'queuedOffline' in value;
}

/**
 * Executa a escrita; sem rede, enfileira e devolve o marcador.
 *
 * A mutacao termina em SUCESSO nos dois caminhos, e isso e o ponto: a escrita
 * otimista ja pintou a tela, e um `onError` faria rollback justamente do que a
 * pessoa acabou de fazer em modo aviao.
 */
export async function withOfflineQueue<T>(
  draft: OpDraft,
  run: () => Promise<T>,
): Promise<T | QueuedOffline> {
  // `navigator.onLine === false` e conclusivo (nao ha interface de rede); o
  // `true` nao garante nada, e por isso a tentativa real continua acontecendo.
  if (typeof navigator !== 'undefined' && navigator.onLine === false) {
    await enqueue(draft);
    return QUEUED_OFFLINE;
  }

  try {
    return await run();
  } catch (err) {
    if (!ehFalhaDeRede(err)) throw err;

    await enqueue(draft);
    return QUEUED_OFFLINE;
  }
}

export interface DrainResult {
  applied: number;
  rejected: SyncResponse['rejected'];
  conflicts: SyncResponse['conflicts'];
  changed: SyncResponse['changed'] | null;
  /** Quanto sobrou na fila. */
  pending: number;
}

const VAZIO: DrainResult = {
  applied: 0,
  rejected: [],
  conflicts: [],
  changed: null,
  pending: 0,
};

/**
 * Envia a fila e recebe o delta (spec 5.1).
 *
 * Nao usa `lib/api.ts` porque precisa distinguir falha de rede de recusa do
 * servidor olhando a resposta crua — e porque tambem roda a partir do evento
 * `online`, fora de qualquer componente.
 */
export async function drainQueue(): Promise<DrainResult> {
  const ops = await pendingOps();
  const since = await readSyncSince();

  // Mesmo com a fila vazia vale sincronizar: e assim que o cliente recebe o que
  // os outros aparelhos mudaram.
  const headers = new Headers({ 'Content-Type': 'application/json' });
  if (DEV_EMAIL) headers.set('X-Dev-Email', DEV_EMAIL);

  let res: Response;
  try {
    res = await fetch(`${API_PREFIX}/sync`, {
      method: 'POST',
      headers,
      credentials: 'include',
      redirect: 'manual',
      body: JSON.stringify({
        since,
        ops: ops.map(({ opId, entity, action, entityId, payload, queuedAt }) => ({
          opId,
          entity,
          action,
          entityId,
          payload,
          queuedAt,
        })),
      }),
    });
  } catch {
    // Continua offline: a fila fica intacta para a proxima tentativa.
    await bumpAttempts(ops.map((op) => op.opId));
    return { ...VAZIO, pending: ops.length };
  }

  /*
    O desvio do Access nao e "servidor recusou": e "ninguem chegou a ver o
    pedido". A fila fica intacta — o que ela precisa e de uma sessao, nao de
    outra tentativa — e a tela passa a dizer isso em vez de mostrar um contador
    de pendencias que nunca baixa (o sintoma observado no Android).
  */
  if (ehDesvioDeLogin(res)) {
    anunciarSessaoExpirada();
    await bumpAttempts(ops.map((op) => op.opId));
    return { ...VAZIO, pending: ops.length };
  }

  anunciarSessaoValida();

  if (!res.ok) {
    // O servidor respondeu e recusou o LOTE inteiro (401 expirado, 500). A fila
    // continua — descartar aqui perderia escrita por um problema que nao e dela.
    await bumpAttempts(ops.map((op) => op.opId));
    return { ...VAZIO, pending: ops.length };
  }

  const body = (await res.json()) as SyncResponse;

  // Aplicadas saem da fila; rejeitadas TAMBEM saem — o servidor ja disse que
  // nunca vao passar, e retenta-las travaria as demais para sempre.
  //
  // Conflitos permanecem ate a pessoa escolher uma versao. Descarta-los seria
  // perder a escrita offline em silencio, que e o que a spec 5.1 proibe.
  await removeOps([...body.applied, ...body.rejected.map((r) => r.opId)]);
  await writeSyncSince(body.now);

  const pending = await countPendingOps();
  notificar();

  return {
    applied: body.applied.length,
    rejected: body.rejected,
    conflicts: body.conflicts,
    changed: body.changed,
    pending,
  };
}

/**
 * Liga a drenagem automatica ao ciclo de vida da aba.
 *
 * `online` cobre o retorno da rede; `visibilitychange` cobre o caso em que o
 * aparelho reconectou com a aba em segundo plano e o evento se perdeu — comum
 * no celular, onde o sistema congela abas ocultas.
 */
export function startAutoSync(onSynced: (result: DrainResult) => void): () => void {
  let rodando = false;

  const tentar = () => {
    if (rodando) return;
    rodando = true;

    void drainQueue()
      .then(onSynced)
      .catch(() => {
        // `drainQueue` ja trata falha de rede devolvendo a fila intacta; cair
        // aqui seria bug, e o navegador registra a rejeicao de qualquer forma.
      })
      .finally(() => {
        rodando = false;
      });
  };

  const aoVoltarAVista = () => {
    if (document.visibilityState === 'visible') tentar();
  };

  window.addEventListener('online', tentar);
  document.addEventListener('visibilitychange', aoVoltarAVista);

  tentar();

  return () => {
    window.removeEventListener('online', tentar);
    document.removeEventListener('visibilitychange', aoVoltarAVista);
  };
}

// ------------------------------------------------------- resolucao de conflito

/**
 * "Manter a minha versao": a operacao enfileirada vence.
 *
 * Recarimba e drena na hora, em vez de deixar para a proxima janela de
 * sincronizacao. Quem acabou de escolher uma versao espera ver o resultado —
 * um conflito que continua na tela depois da escolha parece que a escolha nao
 * pegou, e a pessoa clica de novo.
 */
export async function manterMinhaVersao(opId: string): Promise<DrainResult> {
  await restampOp(opId, Date.now());
  return drainQueue();
}

/**
 * "Ficar com a do servidor": a operacao local e descartada.
 *
 * Sai da fila, e por isso NAO ha desfazer: a escrita deixa de existir em
 * qualquer lugar. E o unico ponto do app onde uma escrita do usuario e
 * abandonada de proposito, e por isso a tela pergunta antes (a excecao que a
 * convencao 6 admite: desfazer aqui nao teria o que devolver).
 *
 * Nao chama `drainQueue`: nao ha nada novo a enviar, e a versao do servidor ja
 * chegou no `changed` do lote que trouxe o conflito. Sincronizar de novo so
 * gastaria um round-trip para receber o que ja esta em cache.
 */
export async function descartarMinhaVersao(opId: string): Promise<void> {
  await removeOps([opId]);
  notificar();
}
